import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Check, AlertTriangle, DollarSign, Calendar, ArrowDownRight } from 'lucide-react';
import toast from 'react-hot-toast';
import costService from '../services/costService';
import expenseAbatesService from '../services/expenseAbatesService';
import projectFinanceService from '../services/projectFinanceService';

const ExpenseAbatementModal = ({ isOpen, onClose, projectId, projectCode, clientName, balance, onRefresh }) => {
  const queryClient = useQueryClient();
  const [selectedCost, setSelectedCost] = useState(null);
  const [abateAmount, setAbateAmount] = useState('');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [entryAmounts, setEntryAmounts] = useState({});
  const [expectedIncomes, setExpectedIncomes] = useState([]);
  const [costs, setCosts] = useState([]);
  const [loadingCosts, setLoadingCosts] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const dateOnly = dateStr.split('T')[0];
    if (dateOnly.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateOnly.split('-');
      return `${day}/${month}/${year}`;
    }
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  // Fetch costs when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCosts();
      fetchExpectedIncomes();
    }
  }, [isOpen, projectId]);

  const fetchCosts = async () => {
    setLoadingCosts(true);
    try {
      // Buscar só despesas personalizadas (CUSTOM)
      const response = await costService.getCostsWithAbates();
      const costsData = response.data || [];
      setCosts(costsData);
    } catch (error) {
      console.error('Erro ao carregar custos:', error);
      toast.error('Erro ao carregar custos');
    } finally {
      setLoadingCosts(false);
    }
  };

  const fetchExpectedIncomes = async () => {
    setLoadingEntries(true);
    try {
      const response = await projectFinanceService.getExpectedIncomes(projectId);
      const incomesData = response.data?.data || response.data || [];
      setExpectedIncomes(incomesData);
    } catch (error) {
      console.error('Erro ao carregar entradas previstas:', error);
      toast.error('Erro ao carregar entradas previstas');
    } finally {
      setLoadingEntries(false);
    }
  };

  // Create abatement mutation
  const createAbateMutation = useMutation({
    mutationFn: (data) => expenseAbatesService.createAbate(data),
    onSuccess: (res) => {
      toast.success(res.message || 'Abate criado com sucesso!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      queryClient.invalidateQueries(['projectInitialBalancesEntries', projectId]);
      queryClient.invalidateQueries(['expenseAbates', projectId]);
      if (onRefresh) onRefresh();
      handleClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao criar abate');
    },
  });

  const handleClose = () => {
    setSelectedCost(null);
    setAbateAmount('');
    setSelectedEntries([]);
    setEntryAmounts({});
    onClose();
  };

  const handleEntryToggle = (entryId) => {
    const entry = expectedIncomes.find(e => e.id === entryId);
    const entryAmount = parseFloat(entry?.expected_amount || entry?.amount || 0);

    setSelectedEntries(prev => {
      if (prev.includes(entryId)) {
        setEntryAmounts(curr => {
          const next = { ...curr };
          delete next[entryId];
          return next;
        });
        return prev.filter(id => id !== entryId);
      } else {
        const currentTotal = Object.entries(entryAmounts)
          .filter(([id]) => id !== String(entryId))
          .reduce((sum, [, val]) => sum + (parseFloat(val) || 0), 0);
        const needed = Math.max(0, (parseFloat(abateAmount) || 0) - currentTotal);
        const autoAmount = needed > 0 ? Math.min(entryAmount, needed) : entryAmount;
        setEntryAmounts(curr => ({
          ...curr,
          [entryId]: autoAmount > 0 ? autoAmount : entryAmount
        }));
        return [...prev, entryId];
      }
    });
  };

  const handleEntryAmountChange = (entryId, amount) => {
    setEntryAmounts(prev => ({
      ...prev,
      [entryId]: amount
    }));
  };

  const getEntryAbateAmount = (entryId) => {
    return entryAmounts[entryId] || 0;
  };

  const totalDistributed = Object.values(entryAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  const remainingAmount = parseFloat(abateAmount) - totalDistributed;

  const extractPaymentMethodFromDescription = (description) => {
    if (!description) return 'cash';
    const match = description.match(/\(([^)]+)\)/);
    if (match) {
      const method = match[1].toLowerCase();
      if (method.includes('dinheiro')) return 'cash';
      if (method.includes('pix')) return 'pix';
      if (method.includes('cartão') || method.includes('cartao')) return 'credit_card';
      if (method.includes('cheque')) return 'cheque';
      if (method.includes('transferência') || method.includes('transferencia')) return 'bank_transfer';
      if (method.includes('boleto')) return 'boleto';
    }
    return 'cash';
  };

  const handleSubmit = () => {
    if (!selectedCost) {
      toast.error('Selecione um custo');
      return;
    }

    const amountNum = parseFloat(abateAmount);
    if (!amountNum || amountNum <= 0) {
      toast.error('Informe o valor do abate');
      return;
    }

    if (selectedEntries.length === 0) {
      toast.error('Selecione pelo menos uma entrada prevista');
      return;
    }

    // Validate total distributed equals abatement amount
    if (Math.abs(totalDistributed - amountNum) > 0.01) {
      toast.error(`Valor distribuído (${formatCurrency(totalDistributed)}) deve ser igual ao valor do abate (${formatCurrency(amountNum)})`);
      return;
    }

    // Build entries array
    const entries = selectedEntries.map(entryId => ({
      expected_income_id: entryId,
      amount_abated: parseFloat(entryAmounts[entryId] || 0)
    })).filter(e => e.amount_abated > 0);

    // Extract payment method from first selected entry description
    const firstEntry = expectedIncomes.find(e => selectedEntries.includes(e.id));
    const payment_method = firstEntry ? extractPaymentMethodFromDescription(firstEntry.description) : 'cash';

    createAbateMutation.mutate({
      project_id: projectId,
      cost_id: selectedCost,
      amount: amountNum,
      payment_method,
      entries
    });
  };

  if (!isOpen) return null;

  const getCostRemainingAmount = (cost) => {
    // Use remaining_amount from the query (already calculated)
    // If not available, fallback to base_price_per_unit
    return cost.remaining_amount || cost.base_price_per_unit || cost.total || 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Abater Despesa de Projeto</h3>
            <p className="text-sm text-gray-500">
              Projeto {projectCode} - {clientName}
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Cost Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custo para Abater *
            </label>
            <select
              value={selectedCost || ''}
              onChange={(e) => {
                const costId = e.target.value ? parseInt(e.target.value) : null;
                setSelectedCost(costId);
                setEntryAmounts({});
                // Auto-fill with remaining amount
                if (costId) {
                  const selectedCostData = costs.find(c => c.id === costId);
                  if (selectedCostData) {
                    setAbateAmount(getCostRemainingAmount(selectedCostData).toString());
                  }
                } else {
                  setAbateAmount('');
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              disabled={loadingCosts}
            >
              <option value="">Selecione o custo</option>
              {costs
                .filter(cost => parseFloat(getCostRemainingAmount(cost)) > 0)
                .map(cost => (
                <option key={cost.id} value={cost.id}>
                  {cost.description} - Restante: {formatCurrency(getCostRemainingAmount(cost))} (Total: {formatCurrency(cost.base_price_per_unit || 0)})
                </option>
              ))}
            </select>
          </div>

          {/* Abatement Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor do Abate (R$) *
            </label>
            <input
              type="number"
              value={abateAmount}
              onChange={(e) => setAbateAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>

          {/* Divider */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <ArrowDownRight className="w-4 h-4 text-green-600" />
              Selecione as Entradas Previstas para Abate
            </h4>
            <p className="text-sm text-gray-500 mb-3">
              Distribua o valor do abate entre as entradas previstas do projeto
            </p>

            {loadingEntries ? (
              <div className="text-center py-4 text-gray-500">Carregando entradas...</div>
            ) : expectedIncomes.length === 0 ? (
              <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                Nenhuma entrada prevista encontrada para este projeto.
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                {expectedIncomes.map(entry => {
                  const isSelected = selectedEntries.includes(entry.id);
                  const entryAmount = parseFloat(entry.amount || entry.expected_amount || 0);
                  const abatedAmount = getEntryAbateAmount(entry.id);

                  return (
                    <div
                      key={entry.id}
                      className={`p-3 rounded-lg border ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleEntryToggle(entry.id)}
                          className="mt-1 w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                                <span>{entry.description || `Entrada ${entry.id}`}</span>
                                {entry.cheque_in_hand && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                                    Em Mãos ✓
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500 flex items-center gap-2">
                                <Calendar className="w-3 h-3" />
                                {formatDate(entry.expected_date || entry.date)}
                                <span className="text-gray-300">|</span>
                                Valor: {formatCurrency(entryAmount)}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              entry.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              entry.status === 'PARTIAL' ? 'bg-blue-100 text-blue-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {entry.status === 'PENDING' ? 'Pendente' :
                               entry.status === 'PARTIAL' ? 'Parcial' : 'Completo'}
                            </span>
                          </div>

                          {isSelected && (
                            <div className="mt-2 pt-2 border-t border-green-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Valor a abater desta entrada (R$)
                              </label>
                              <input
                                type="number"
                                value={abatedAmount || ''}
                                onChange={(e) => handleEntryAmountChange(entry.id, e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-green-500"
                                min="0"
                                max={entryAmount}
                                step="0.01"
                                placeholder="0.00"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Máximo: {formatCurrency(entryAmount)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Summary */}
          {selectedEntries.length > 0 && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Valor do Abate:</span>
                <span className="font-medium">{formatCurrency(parseFloat(abateAmount) || 0)}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-600">Valor Distribuído:</span>
                <span className="font-medium">{formatCurrency(totalDistributed)}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-600">Restante:</span>
                <span className={`font-medium ${Math.abs(remainingAmount) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(remainingAmount)}
                </span>
              </div>
              {Math.abs(remainingAmount) >= 0.01 && (
                <div className="mt-2 flex items-center gap-2 text-amber-600 text-xs">
                  <AlertTriangle className="w-4 h-4" />
                  O valor distribuído deve ser igual ao valor do abate
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={createAbateMutation.isPending || !selectedCost || !abateAmount || selectedEntries.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" />
            {createAbateMutation.isPending ? 'Criando...' : 'Confirmar Abate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseAbatementModal;